
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Facebook, Linkedin } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Signup = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [facebookError, setFacebookError] = useState("");
  const [linkedinError, setLinkedinError] = useState("");
  const { signInWithGoogle, signInWithFacebook, signInWithLinkedIn, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setGeneralError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithGoogle();
    } catch (error: any) {
      console.error("Google signup error:", error);
      setGeneralError(error.message || "An error occurred with Google signup");
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      setIsFacebookLoading(true);
      setGeneralError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithFacebook();
    } catch (error: any) {
      console.error("Facebook signup error:", error);
      
      if (error.message?.includes("provider is not enabled")) {
        setFacebookError("Facebook login is not properly configured. The app administrator needs to complete the Facebook app setup.");
      } else {
        setGeneralError(error.message || "An error occurred with Facebook signup");
      }
      
      setIsFacebookLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    try {
      setIsLinkedInLoading(true);
      setGeneralError("");
      setFacebookError("");
      setLinkedinError("");
      await signInWithLinkedIn();
    } catch (error: any) {
      console.error("LinkedIn signup error:", error);
      
      if (error.message?.includes("provider is not enabled")) {
        setLinkedinError("LinkedIn login is not properly configured. The app administrator needs to complete the LinkedIn app setup.");
      } else {
        setGeneralError(error.message || "An error occurred with LinkedIn signup");
      }
      
      setIsLinkedInLoading(false);
    }
  };

  return (
    <Layout title="Sign Up | Freelance With Andy" path="/signup">
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <Card className="w-full max-w-md shadow-xl border">
          <CardHeader className="pb-2">
            <CardTitle className="text-3xl font-bold text-center">
              <span className="text-[#34BF49]">Create an Account</span>
            </CardTitle>
            <CardDescription className="text-center text-gray-600 mt-4">
              Join Freelance With Andy to access exclusive content
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            {generalError && (
              <Alert variant="destructive">
                <AlertDescription>{generalError}</AlertDescription>
              </Alert>
            )}
            
            {facebookError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {facebookError}
                  <p className="mt-2 text-sm">
                    <strong>Note for developers:</strong> You need to add your app's domain to the Facebook app configuration 
                    in the Facebook Developer Console.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {linkedinError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {linkedinError}
                  <p className="mt-2 text-sm">
                    <strong>Note for developers:</strong> You need to enable and configure the LinkedIn provider 
                    in your Supabase Authentication settings.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 gap-4">
              <Button 
                type="button" 
                className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm transition-all"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                <span className="font-medium">Continue with Google</span>
              </Button>

              <Button 
                type="button" 
                className="w-full h-12 flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white transition-all"
                onClick={handleFacebookSignIn}
                disabled={isFacebookLoading}
              >
                {isFacebookLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Facebook className="h-5 w-5" />
                )}
                <span className="font-medium">Continue with Facebook</span>
              </Button>

              <Button 
                type="button" 
                className="w-full h-12 flex items-center justify-center gap-3 bg-[#0A66C2] hover:bg-[#004182] text-white transition-all"
                onClick={handleLinkedInSignIn}
                disabled={isLinkedInLoading}
              >
                {isLinkedInLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Linkedin className="h-5 w-5" />
                )}
                <span className="font-medium">Continue with LinkedIn</span>
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center pb-8">
            <p className="text-sm text-center text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-green-600 hover:text-green-700 font-medium hover:underline transition-colors">
                Log in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
};

export default Signup;
